import React, { useState, useEffect } from "react";
import { Collapse, Card, Button } from "react-bootstrap";
import { ThreadMessage, ThreadAttachment } from "@preveil-api";
import { IFRAME_HEAD, Account, useAppDispatch, Message, MailErrorMessages, MessageHandlerDisplayType, MailThreadMessage, MailMessageBody, Attachment } from "src/common";
import { Loading, Icon, InlineIframe } from "src/components";
import { AttachmentBadge, AttachmentBulkSave } from ".";
import { uiActions } from "src/store";
import _ from "lodash";

type AllProps = {
  is_web: boolean;
  current_account: Account;
  collection_id: string;
  default_open: boolean;
  message: ThreadMessage;
  messages: ThreadMessage[];
  active_attachment: boolean;
  handleGetBody: (message: ThreadMessage) => void;
  handleAttachmentModal: (_attachments: Attachment[]) => void;
}

function MessageBodyComponent(props: AllProps) {
  const { active_attachment, current_account, collection_id, is_web, message, messages, default_open, handleGetBody, handleAttachmentModal } = props;
  const dispatch = useAppDispatch();
  const [message_body, setMessageBody] = useState<string | null>(null);
  const [open, setOpen] = useState<boolean>(true);
  const [previous_message, setPreviousMessage] = useState<ThreadMessage>();
  const [previous_message_body, setPreviousMessageBody] = useState<string | null>(null);
  const [open_prev, setOpenPrev] = useState<boolean>(true);

  // Description: Send message body errors directly to logger
  useEffect(() => {
    !!message.error &&
      dispatch(uiActions.handleRequestErrors(new Message(MailErrorMessages.error_getting_message, MessageHandlerDisplayType.logger),
        { error: message.error, message }));
  }, []);

  // Description: Set to close previous as default state, after rendering the iframe (for height purposes)
  useEffect(() => {
    handleMessageBody(message.html);
  }, [messages]);

  // Description: Sanitize html and create MessageBody content
  function handleMessageBody(_message_body: string | null) {
    // NOTE: Accept empty strings:
    if (_message_body !== null && _message_body !== undefined) {
      const _previous_message = MailThreadMessage.getPreviousMessage(message, messages);
      const mail_message_body = new MailMessageBody(message, _previous_message);
      const processed_message = mail_message_body.processed_message;
      setMessageBody(processed_message.message_body || "");
      if (!!_previous_message && !mail_message_body.blockquote_modified) {
        // NOTE: Guard on html identity to avoid re-processing when messages update but this reply hasn't changed.
        _previous_message.html !== previous_message?.html && handlePreviousMessageBody(_previous_message);
      } else {
        // NOTE: Clear any stale previous-message state from prior navigation. Covers two cases:
        // (1) no previous message for this reply, and (2) the user annotated the blockquote — the quote
        // is already visible inline, so the collapsible would duplicate the quoted content.
        setPreviousMessage(undefined);
        setPreviousMessageBody(null);
      }
    }
    setOpen(default_open || !!_message_body);
  }

  // Description: Sanitize previous messages body
  function handlePreviousMessageBody(_previous_message: ThreadMessage) {
    setPreviousMessage(_previous_message);
    const __previous_message = MailThreadMessage.getPreviousMessage(_previous_message, messages);
    const processed_message = new MailMessageBody(_previous_message, __previous_message).processed_message;
    if (!!processed_message) {
      const _previous_message_body = MailThreadMessage.buildPreviousMailBody(processed_message);
      setPreviousMessageBody(!!_previous_message_body ? _previous_message_body : `<p class='error'>${MailErrorMessages.error_getting_message}</p>`);
    }
  }

  // Description: trigger open of a body for call when message_body is not avail
  function handleOpenBody(new_open: boolean) {
    (!message.message_body && new_open) && handleGetBody(message);
    setOpen(new_open);
  }

  return <>
    <Collapse in={open}>
      <div>
        <Card.Body>
          {
            message_body !== null ?
              <InlineIframe
                iframeHead={IFRAME_HEAD}
                srcDoc={message_body}
                title="Mail Body"
                height="100%"
                width="100%"
                sandbox="allow-same-origin allow-popups allow-top-navigation allow-popups-to-escape-sandbox allow-scripts"
              /> :
              <Loading noLogo className="in-place my-0" message="Loading content..." />
          }
          {
            (!!previous_message) &&
            <div className="previous_mail">
              <Button variant="icon" size="sm" onClick={() => setOpenPrev(!open_prev)} title={open_prev ? "View less message information" :
                "View more information about message"}>
                {open_prev ? <span>Show less...</span> : <span>Show more...</span>}
              </Button>
              <Collapse in={open_prev}>
                <div>
                  {
                    !!previous_message_body ?
                      <InlineIframe
                        iframeHead={IFRAME_HEAD}
                        srcDoc={previous_message_body}
                        title="Mail Body"
                        height="100%"
                        width="100%"
                        sandbox="allow-same-origin allow-popups allow-top-navigation allow-popups-to-escape-sandbox allow-scripts"
                      /> : <Loading noLogo className="in-place my-0" message="Loading content..." />
                  }
                </div>
              </Collapse>
            </div>
          }
        </Card.Body>
        {
          (!!message.attachments && message.attachments.length > 0) &&
          <Card.Footer className="attachments">
            {
              _.map(message.attachments, (attachment: ThreadAttachment, i: number) => {
                return <AttachmentBadge
                  key={`attachment_${i}`}
                  attachment={attachment}
                  {...props}
                  // NOTE: Pass only single Attachment as an Array (length = 1) to Attachment Modal
                  handleAttachmentModal={(_attachment: Attachment) => handleAttachmentModal([_attachment])}
                />;
              })
            }
            {
              (message.attachments.length > 1) &&
              <AttachmentBulkSave
                current_account={current_account}
                is_web={is_web}
                collection_id={message.message_collection_id || collection_id}
                active_attachment={active_attachment}
                thread_attachments={message.attachments}
                handleAttachmentModal={handleAttachmentModal} />
            }
          </Card.Footer>
        }
      </div>
    </Collapse>
    <Button variant="icon" onClick={() => handleOpenBody(!open)} title={open ? "View Less" : "View More"}>
      {
        open ? <Icon className="ficon-chevron-up" /> : <Icon className="ficon-chevron-down" />
      }
    </Button>
  </>;
}

export default React.memo(MessageBodyComponent);
